import { ADMIN_API_PREFIX, fillPath, route } from "./routes.js";
import { z } from "zod";
import { RIGHTS_CERTIFICATE_KID_PATTERN, kernelRightSchema } from "@zion/sdk-contracts";
//#region src/kernel-consents.ts
/**
* `/v1/admin/kernel-consents`: the org owner's kernel consents (kernel apps, WS-2b,
* `design-docs/WS-2.md` section 3.4, locked decision 1).
*
* A kernel consent accepts one kernel right for one app and one code-signing key. A machine bearer
* of that app uses the right only while an active consent holds the same app, right and key
* (WS-12b `KernelConsentReader`, WS-19 guard). A new code-signing key asks for a new consent.
*
* Producer: core-api's kernel module (WS-12b) in `cloud/dp/core-api/src/kernel/consents/`.
* Consumers: the kernel shell (WS-15) and the org admin app (EX-1). Only a holder of the org role
* `owner` may call the three routes; core-api answers 403 `kernel_owner_required` to anyone else.
*
* Kernel class (`CONTRACT_ROUTE_KERNEL_CLASS` in `route-census.ts`): right `authority.grants`, the
* list at tier 0, the create and the revoke at tier 1 (`design-docs/WS-2.md` open questions 7 and 8).
* The three routes sit in the kernel fold `KERNEL_CONTRACT_ROUTES`.
*
* Direction rule (R-28): each request schema is `z.strictObject` and refuses an unknown key; each
* response schema is `z.object` and strips one. No schema here carries a `.transform()`, so
* `createOpenApiDocument()` can represent each one.
*/
/** The collection path. It has no parameter, so the OpenAPI form and the request form agree. */
const ADMIN_KERNEL_CONSENTS_PATH = `${ADMIN_API_PREFIX}/kernel-consents`;
/** One consent, in OpenAPI `{param}` form. Fill it with {@link adminKernelConsentPath}. */
const ADMIN_KERNEL_CONSENT_PATH_TEMPLATE = `${ADMIN_KERNEL_CONSENTS_PATH}/{id}`;
/**
* A code-signing key id: the WS-7a `kid`, 1 to 128 base64url characters with no padding
* (`RIGHTS_CERTIFICATE_KID_PATTERN` of `@zion/sdk-contracts`). An RFC 7638 thumbprint has 43
* characters. WS-1a exports the pattern and no schema, so this module holds the field (index
* defaults row 2). The length check runs first and aborts the parse, so an over-length id gets one
* `too_big` issue and no pattern run.
*/
const codeSigningKeyIdField = z.string().max(128, { abort: true }).regex(RIGHTS_CERTIFICATE_KID_PATTERN);
/**
* `GET /v1/admin/kernel-consents`. Status 200 with {@link listKernelConsentsResponseSchema}: the
* active consents of the caller's org and the open asks. No query. Kernel class `authority.grants`
* tier 0.
*/
const LIST_KERNEL_CONSENTS_ROUTE = route("GET", ADMIN_KERNEL_CONSENTS_PATH);
/**
* `POST /v1/admin/kernel-consents`. Body {@link createKernelConsentBodySchema}. Status 201 with
* one {@link kernelConsentSchema} row, and one audit event `kernel_consent_granted`. Kernel class
* `authority.grants` tier 1. The refusals after the guard, in this order:
*
* 1. 404 `kernel_consent_target_not_found`: no live certificate of that app names that right.
* 2. 409 `kernel_consent_key_mismatch`: the live certificate names another code-signing key.
* 3. 409 `kernel_consent_exists`: an active consent holds the same app, right and key.
*
* The body names the key, so the owner accepts the key that the page showed. A key that changed
* since then answers 409.
*/
const CREATE_KERNEL_CONSENT_ROUTE = route("POST", ADMIN_KERNEL_CONSENTS_PATH);
/**
* `DELETE /v1/admin/kernel-consents/{id}`. Status 200 with
* {@link revokeKernelConsentResponseSchema}, and one audit event `kernel_consent_revoked`. core-api
* marks the row revoked and never deletes it. An id outside the caller's org, or a row already
* revoked, answers 404 `kernel_consent_not_found`. Kernel class `authority.grants` tier 1.
*/
const REVOKE_KERNEL_CONSENT_ROUTE = route("DELETE", ADMIN_KERNEL_CONSENT_PATH_TEMPLATE);
/**
* The body of {@link CREATE_KERNEL_CONSENT_ROUTE}, strict:
*
* - `appId`: the `release.apps_catalog` uuid of the app;
* - `right`: one of the 14 kernel rights of `kernelRightSchema`; the parse refuses an unknown right;
* - `codeSigningKeyId`: the code-signing key id that the page showed, 1 to 128 base64url characters.
*
* Any other key fails.
*/
const createKernelConsentBodySchema = z.strictObject({
	appId: z.uuid(),
	right: kernelRightSchema,
	codeSigningKeyId: codeSigningKeyIdField
});
/**
* One active consent:
*
* - `id`: the `auth.kernel_consents` uuid;
* - `appId`, `right`, `codeSigningKeyId`: what the owner accepted;
* - `grantedBy`: the uuid of the owner who accepted it;
* - `grantedAt`: an ISO 8601 UTC time.
*/
const kernelConsentSchema = z.object({
	id: z.uuid(),
	appId: z.uuid(),
	right: kernelRightSchema,
	codeSigningKeyId: codeSigningKeyIdField,
	grantedBy: z.uuid(),
	grantedAt: z.iso.datetime()
});
/**
* One open ask: a kernel right that a live rights certificate on this DP names for an app, with no
* active consent for that key.
*
* - `appId`: the app uuid;
* - `appDisplayName`: the app name for the page, a non-empty text;
* - `appVersion`: the version whose certificate names the right, a non-empty text;
* - `right`: the kernel right;
* - `codeSigningKeyId`: the key that the consent must name.
*/
const kernelConsentAskSchema = z.object({
	appId: z.uuid(),
	appDisplayName: z.string().min(1),
	appVersion: z.string().min(1),
	right: kernelRightSchema,
	codeSigningKeyId: codeSigningKeyIdField
});
/** The 200 body of {@link LIST_KERNEL_CONSENTS_ROUTE}: `{ consents, asks }`, two arrays. */
const listKernelConsentsResponseSchema = z.object({
	consents: z.array(kernelConsentSchema),
	asks: z.array(kernelConsentAskSchema)
});
/** The 200 body of {@link REVOKE_KERNEL_CONSENT_ROUTE}: the row id and its ISO 8601 revoke time. */
const revokeKernelConsentResponseSchema = z.object({
	id: z.uuid(),
	revokedAt: z.iso.datetime()
});
/** The four refusal codes of the consent routes, in the order of the route documents above. */
const KERNEL_CONSENT_ERROR_CODES = Object.freeze([
	"kernel_consent_target_not_found",
	"kernel_consent_key_mismatch",
	"kernel_consent_exists",
	"kernel_consent_not_found"
]);
/**
* Fill {@link ADMIN_KERNEL_CONSENT_PATH_TEMPLATE} with one consent id.
*
* @param consentId the consent uuid, as {@link kernelConsentSchema} `id` publishes it.
* @returns the request path.
* @throws a `ZodError` when `consentId` is not a uuid, so `..`, a slash or an empty string never
*   reaches a URL. Pure.
*/
function adminKernelConsentPath(consentId) {
	return fillPath(ADMIN_KERNEL_CONSENT_PATH_TEMPLATE, { id: consentId }, { id: z.uuid() });
}
/** Every route this file declares, for the kernel fold of `route-census.ts`. */
const kernelConsentsRoutes = Object.freeze([
	LIST_KERNEL_CONSENTS_ROUTE,
	CREATE_KERNEL_CONSENT_ROUTE,
	REVOKE_KERNEL_CONSENT_ROUTE
]);
//#endregion
export { ADMIN_KERNEL_CONSENTS_PATH, ADMIN_KERNEL_CONSENT_PATH_TEMPLATE, CREATE_KERNEL_CONSENT_ROUTE, KERNEL_CONSENT_ERROR_CODES, LIST_KERNEL_CONSENTS_ROUTE, REVOKE_KERNEL_CONSENT_ROUTE, adminKernelConsentPath, createKernelConsentBodySchema, kernelConsentAskSchema, kernelConsentSchema, kernelConsentsRoutes, listKernelConsentsResponseSchema, revokeKernelConsentResponseSchema };

//# sourceMappingURL=kernel-consents.js.map